import {User} from '@hconnect/apiclient'
import {Button, TextSmallRegular} from '@hm/fundamental'
import {mdiDeleteOutline} from '@mdi/js'
import {AxiosError} from 'axios'
import React, {useState} from 'react'

import {
  RoleConfiguration,
  useRolesConfigurationForUserId
} from '../../common/hooks/useRolesConfiguration'
import {RoleCustomerChip} from '../../components/Role/RoleCustomerChip'
import {RoleCustomerLookupChip} from '../../components/Role/RoleCustomerLookupChip'
import {useCustomersList} from '../../hooks/useCustomer'
import {useTranslation} from '../../hooks/useTranslation'
import {CustomersScopes, GroupedRoleAssignment} from '../../modules/ManageUsers.selectors'

import {ContractsModal} from './ContractsModal'
import {CustomersAndProjectsSitesModal} from './CustomersScopesModal'
import {isProjectsCountry} from './utils'

type CustomersScopesProps = {
  groupedRoleAssignment: GroupedRoleAssignment
  isEditing?: boolean
  handleDeleteCustomer?: (customerScopes: CustomersScopes) => void
  handleSaveCustomerId?: (customerScopes: CustomersScopes[]) => void
  handleSaveContractIds?: (contractIds: string[]) => void
  loggedInUserProfile?: User | null
}

export const CustomersAndProjectsSites = ({
  groupedRoleAssignment,
  isEditing = false,
  handleDeleteCustomer,
  handleSaveCustomerId,
  handleSaveContractIds,
  loggedInUserProfile
}: CustomersScopesProps) => {
  const {t} = useTranslation()
  const {customersScopes} = groupedRoleAssignment.variantDataScopes
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null)
  const [editingCustomerIdForContracts, setEditingCustomerIdForContracts] = useState<string | null>(
    null
  )

  const {rolesByType} = useRolesConfigurationForUserId(loggedInUserProfile)

  const countryId = groupedRoleAssignment.commonDataScopes.countryId
  const orgUnitId = groupedRoleAssignment.commonDataScopes.orgUnitId

  const {
    data: customers,
    isLoading: areCustomersLoading,
    isError: isCustomersError,
    error: customersError
  } = useCustomersList(
    customersScopes.map((scopes) => scopes.customerId),
    countryId as string,
    orgUnitId as string
  )

  const roleConfiguration: RoleConfiguration | undefined =
    rolesByType?.[groupedRoleAssignment.roleType]

  if (isEditing && !roleConfiguration) {
    return null
  }
  const roleDataScopeNames = roleConfiguration
    ? new Set(roleConfiguration.dataScopes.map((d) => d.dataScope))
    : new Set()

  const hasProjectSiteIdScopeInConfiguration =
    roleDataScopeNames.has('siteIds') ||
    roleDataScopeNames.has('projectIds') ||
    roleDataScopeNames.has('siteId') ||
    roleDataScopeNames.has('projectId')

  const hasContractIdScopeInConfiguration =
    roleDataScopeNames.has('contractIds') && roleDataScopeNames.has('contractIds')

  const canSelectProjects = roleDataScopeNames.has('projectIds')
  const canSelectSites = roleDataScopeNames.has('siteIds')

  if (customersScopes.length === 0) {
    return null
  }

  return (
    <>
      {!!editingCustomerId && (
        <CustomersAndProjectsSitesModal
          canSelectProjects={canSelectProjects}
          canSelectSites={canSelectSites}
          open={!!editingCustomerId}
          initialCustomer={editingCustomerId}
          roleType={groupedRoleAssignment.roleType}
          commonDataScopes={groupedRoleAssignment.commonDataScopes}
          customersScopes={groupedRoleAssignment.variantDataScopes.customersScopes}
          onSave={handleSaveCustomerId}
          onClose={() => {
            setEditingCustomerId(null)
          }}
        />
      )}
      {!!editingCustomerIdForContracts && (
        <ContractsModal
          open={Boolean(editingCustomerIdForContracts)}
          onClose={() => {
            setEditingCustomerIdForContracts(null)
          }}
          customerId={editingCustomerIdForContracts}
          customersScopes={groupedRoleAssignment.variantDataScopes.customersScopes}
          handleSaveContractIds={handleSaveContractIds}
        />
      )}
      <div className="flex flex-col gap-2">
        {/* eslint-disable-next-line complexity */}
        {customersScopes.map((customerScopes) => {
          const {customerId, siteIds, projectIds, contractIds} = customerScopes

          const isOrderPlacerRole = groupedRoleAssignment.roleType === 'ORDER_PLACER'
          const canEditSites = !isOrderPlacerRole || contractIds.length === 0
          // We do not care about projects in the condition below as those cannot be selected
          const canEditContracts = !isOrderPlacerRole || siteIds.length === 0

          const projectsSitesLabel = (() => {
            const count = siteIds.length + projectIds.length
            const showsSites = countryId && !isProjectsCountry(countryId as string)
            if (count === 0) {
              if (isOrderPlacerRole) {
                return t('roleAssignment.destinationsNumberPlaceholder_all')
              }

              return showsSites
                ? t('roleAssignment.sitesNumberPlaceholder_all')
                : t('roleAssignment.projectsSitesNumberPlaceholder_all')
            }

            if (isOrderPlacerRole) {
              return t('roleAssignment.destinationsNumberPlaceholder', {count: siteIds.length})
            }

            return t(
              showsSites
                ? 'roleAssignment.sitesNumberPlaceholder'
                : 'roleAssignment.projectsSitesNumberPlaceholder',
              {count: siteIds.length + projectIds.length}
            )
          })()

          return (
            <div key={customerId} className="flex gap-2">
              <div className="flex-1 border-1px border-border_highlight_10 rounded-lg py-2 px-3 flex flex-col items-start">
                <div>
                  {customers && (
                    <RoleCustomerChip
                      key={customerId}
                      customer={customers?.find(
                        (c) => c.customerId.toLowerCase() === customerId.toLowerCase()
                      )}
                      isCustomerLoading={areCustomersLoading}
                      isError={isCustomersError}
                      error={customersError as AxiosError}
                    />
                  )}
                  {isCustomersError && customersScopes.length <= 10 && (
                    <RoleCustomerLookupChip key={customerId} customerId={customerId} />
                  )}
                </div>
                <div className="flex items-center">
                  {(siteIds.length ||
                    projectIds.length ||
                    hasProjectSiteIdScopeInConfiguration) && (
                    <div>
                      <TextSmallRegular className="text-foreground font-normal">
                        {projectsSitesLabel}
                      </TextSmallRegular>
                      {isEditing && (
                        <Button
                          onClick={() => {
                            setEditingCustomerId(customerScopes.customerId)
                          }}
                          disabled={!canEditSites}
                          variant="tertiary"
                          data-test-id="roleAssignment.editCustomersScopes"
                          className="px-2"
                          type="button"
                        >
                          {t('roleAssignment.editCustomersScopes')}
                        </Button>
                      )}
                    </div>
                  )}
                  {(siteIds.length || projectIds.length || hasProjectSiteIdScopeInConfiguration) &&
                    (contractIds.length || hasContractIdScopeInConfiguration) && (
                      <div className="mx-2 h-6 border-l border-border_highlight_10" />
                    )}
                  {(contractIds.length || hasContractIdScopeInConfiguration) && (
                    <div>
                      <TextSmallRegular className="text-foreground font-normal">
                        {t(
                          contractIds.length === 0
                            ? 'roleAssignment.contractsPlaceholder_all'
                            : 'roleAssignment.contractsPlaceholder',
                          {
                            contracts: customerScopes.contractIds.join(', ')
                          }
                        )}
                      </TextSmallRegular>
                      {isEditing && (
                        <Button
                          onClick={() => {
                            setEditingCustomerIdForContracts(customerScopes.customerId)
                          }}
                          disabled={!canEditContracts}
                          variant="tertiary"
                          data-test-id="roleAssignment.editContracts"
                          type="button"
                          className="px-2"
                        >
                          {t('roleAssignment.editContracts')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {isEditing && (
                <Button
                  type="button"
                  variant="secondary"
                  icon={mdiDeleteOutline}
                  onClick={() => {
                    if (handleDeleteCustomer) {
                      handleDeleteCustomer(customerScopes)
                    }
                  }}
                >
                    {t('roleAssignment.remove')}
                </Button>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}
